import { useEffect, useState } from "react";
import RequirePermission from "../../../auth/RequirePermission";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import { fetchInventoryQualityRules } from "../../../services/automapInventoryQualityApi";
import { fetchVehicleRiskRules } from "../../../services/automapVehicleRiskApi";
import { trackInventoryQualityEvent } from "../../../constants/automapInventoryQualityConstants";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { InventoryQualityNav } from "./inventoryQualityShared";

const RULE_LABELS = {
  staleAfterDays: "Obsoleto después de (días)",
  severeStaleAfterDays: "Obsoleto severo después de (días)",
  updateStaleDays: "Sin confirmación ni actualización (días)",
  lowQualityThreshold: "Umbral calidad baja",
  missingPhotosEnabled: "Issue sin fotos habilitado",
  missingDescriptionEnabled: "Issue sin descripción habilitado",
  priceMinimumCop: "Precio mínimo (COP)",
  highMileageKm: "Kilometraje alto (km)",
  comparablePriceLowPct: "Precio bajo vs mediana (%)",
  comparablePriceHighPct: "Precio alto vs mediana (%)",
  minComparablesRequired: "Mínimo comparables requeridos",
};

const FAIR_PRICE_RULE_LABELS = {
  minComparablesLowConfidence: "Fair price · mínimo comparables (baja confianza)",
  minComparablesMediumHighConfidence: "Fair price · mínimo comparables (media/alta)",
  belowMarketThresholdPct: "Fair price · umbral por debajo del mercado (%)",
  fairPriceMaxPct: "Fair price · umbral precio justo (±%)",
  slightlyHighMaxPct: "Fair price · umbral un poco alto (%)",
  yearRangePreferred: "Fair price · rango año preferido (±)",
  yearRangeFallback: "Fair price · rango año fallback (±)",
  minValidPriceCop: "Fair price · precio mínimo válido (COP)",
};

const RISK_RULE_LABELS = {
  staleListingPoints: "Riesgo · puntos publicación obsoleta",
  missingPhotosPoints: "Riesgo · puntos sin fotos",
  missingPricePoints: "Riesgo · puntos sin precio",
  priceUnusuallyLowPoints: "Riesgo · puntos precio bajo",
  openTrustSafetyCasePoints: "Riesgo · puntos caso trust/safety",
  multipleReportThreshold: "Riesgo · umbral múltiples reportes",
  riskLevelLowMax: "Riesgo · máximo nivel bajo",
  riskLevelMediumMax: "Riesgo · máximo nivel medio",
};

function AdminInventoryQualityRulesPageContent() {
  const [rules, setRules] = useState(null);
  const [riskRules, setRiskRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchInventoryQualityRules()
      .then((data) => {
        setRules(data.rules);
        trackInventoryQualityEvent("inventory_quality_rules_viewed");
      })
      .catch((err) => setError(err.message || "No se pudieron cargar las reglas."))
      .finally(() => setLoading(false));
    fetchVehicleRiskRules()
      .then((data) => setRiskRules(data.rules))
      .catch(() => setRiskRules(null));
  }, []);

  return (
    <AdminPageLayout className="admin-inventory-quality-page">
      <AdminAutomotiveNav active="inventory-quality" />
      <AdminPageHeader
        eyebrow="AutoMapa · Calidad"
        title="Reglas de calidad"
        description="Umbrales actuales para scoring, obsolescencia y detección de atípicos."
        navigation={<InventoryQualityNav active="rules" />}
      />
      <main className="admin-console__main">
        {error ? <AdminStatePanel variant="empty" message={error} /> : null}
        {loading ? (
          <AdminStatePanel variant="empty" message="Cargando reglas…" />
        ) : rules ? (
          <>
            <p className="muted">Configuración de solo lectura. Edición persistente: TODO Milestone 20+.</p>
            <dl className="admin-detail-dl">
              {Object.entries(RULE_LABELS).map(([key, label]) => (
                <div key={key} style={{ display: "contents" }}>
                  <dt>{label}</dt>
                  <dd>{typeof rules[key] === "boolean" ? (rules[key] ? "Sí" : "No") : String(rules[key] ?? "—")}</dd>
                </div>
              ))}
            </dl>
            {rules.fairPriceRules ? (
              <>
                <h3 style={{ marginTop: "1.5rem" }}>Reglas de precio justo</h3>
                <dl className="admin-detail-dl">
                  {Object.entries(FAIR_PRICE_RULE_LABELS).map(([key, label]) => (
                    <div key={key} style={{ display: "contents" }}>
                      <dt>{label}</dt>
                      <dd>{String(rules.fairPriceRules[key] ?? "—")}</dd>
                    </div>
                  ))}
                </dl>
              </>
            ) : null}
            {riskRules ? (
              <>
                <h3 style={{ marginTop: "1.5rem" }}>Reglas de indicadores de riesgo</h3>
                <p className="muted">Configuración de solo lectura. Edición persistente: TODO Milestone 24+.</p>
                <dl className="admin-detail-dl">
                  {Object.entries(RISK_RULE_LABELS).map(([key, label]) => (
                    <div key={key} style={{ display: "contents" }}>
                      <dt>{label}</dt>
                      <dd>{String(riskRules[key] ?? "—")}</dd>
                    </div>
                  ))}
                </dl>
              </>
            ) : null}
          </>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}

export default function AdminInventoryQualityRulesPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminInventoryQualityRulesPageContent />
    </RequirePermission>
  );
}
